// import type { EditorialContent, EditorialSponsor, MediaImage, TicketingInfo } from '../types';

import type { EditorialContent, EditorialSponsor, MediaImage } from '@shared-types/editorial.types';
import type { TicketingInfo } from '@shared-types/ticketing.types';
import { stripHtml } from '@utilities/dom';

export type { EditorialContent, MediaImage };

export const slugSeparator = '--';
export const convertToSlug = (id: string, text: string) =>
  text
    ? `${id}${slugSeparator}${text
        .toLowerCase()
        .replace(/[^\w ]+/g, '')
        .replaceAll(' ', '-')}`
    : id;

export const getIdFromPath = (url = location.pathname) => {
  return new RegExp(`\\/([\\d\\w-]+)${slugSeparator}`).exec(url)?.[1];
};

export const removeSlug = (url = location.pathname) => {
  return url.replace(new RegExp(`${slugSeparator}[\\d\\w-]+`), '');
};

export const resolvePathUrl = (url: string, format: string) => {
  if (!url?.includes('/')) return null;

  const arr = url.split('/');
  arr.splice(arr.length - 1, 0, 'format', format);
  return `${window.cobaltImgBaseUrl}${arr.join('/')}`;
};

export const getImage = (mediaItem): MediaImage => {
  const figure = mediaItem?.figure?.[0];

  if (!figure?.img) return null;

  const photo: MediaImage = {
    id: undefined,
    credits: figure.figcredits,
    description: figure.figcaption,
    title: figure.figcaption,
    image: {},
  };

  figure.img.forEach((i) => {
    const format = i['emk-softCrop'];

    if (format?.endsWith('px-Custom')) {
      const customFormat = i['emk-softCrop'].replace('px-Custom', '');
      photo.image.custom = {
        [customFormat]: `${window.cobaltImgBaseUrl}${i.src}`,
      };
    }

    switch (i['emk-softCrop']) {
      case 'Wide':
        photo.image.wide = {
          w1: resolvePathUrl(i.src, 'wide1'),
          w2: resolvePathUrl(i.src, 'wide2'),
          w3: resolvePathUrl(i.src, 'wide3'),
          w4: resolvePathUrl(i.src, 'wide4'),
        };
        photo.id = i['data-id'];
        break;
      case 'Square':
        photo.image.square = {
          s1: resolvePathUrl(i.src, 'square1'),
          s2: resolvePathUrl(i.src, 'square2'),
          s3: resolvePathUrl(i.src, 'square3'),
          s4: resolvePathUrl(i.src, 'square4'),
        };
        break;
      case 'Portrait':
        photo.image.portrait = {
          p1: resolvePathUrl(i.src, 'portrait1'),
          p2: resolvePathUrl(i.src, 'portrait2'),
          p3: resolvePathUrl(i.src, 'portrait3'),
        };
        break;
      case 'Ultrawide':
        photo.image.ultraWide = {
          ultrawide: resolvePathUrl(i.src, 'ultrawide'),
        };
        break;
      case 'Free':
        photo.image.free = {
          f1: resolvePathUrl(i.src, 'free1'),
          f2: resolvePathUrl(i.src, 'free2'),
          f3: resolvePathUrl(i.src, 'free3'),
          f4: resolvePathUrl(i.src, 'free4'),
        };
        break;
    }
  });

  return photo;
};

const getAnswers = (item) => {
  const answers = item['_json'].filter((x) => x['_type'] === 'ANSWERS')?.[0];
  return answers['_json']?.map((answer) => ({
    text: answer._text,
    id: answer['_attributes']?.['data-answer-id'],
  }));
};

export const getPoll = (item) => {
  const poll = { pollId: null, question: null, answers: null };

  poll.question = item['_json'].filter((x) => x['_type'] === 'QUESTION')?.[0]?._text;
  poll.answers = getAnswers(item);
  poll.pollId = item['_attributes']?.['data-poll-id'];

  return poll;
};

const parseNumber = (value: any) => {
  if (!value) return 0;
  const result = parseInt(value);
  return isNaN(result) ? 0 : result;
};

const getByType = (arr, type) => arr?.find((o) => o._type === type || o.type === type);

const mapByType = (el) => {
  if (!el) return;
  switch (el._type) {
    case 'PHOTO':
      return getImage(el);
    case 'POLL':
      return getPoll(el);
    default:
      return el;
  }
};

export const mapEditorialContent = (data: any, nodes?: { [id: string]: string }, isList = false): EditorialContent => {
  if (!data) return {};

  const type = data.sys.type;
  const document = data.files['content.json']?.data.document;
  const mediaInfo = data.attributes.mediaInfo;
  const hideFromWeb = data.attributes.hideFromWeb;
  const ctaLabel = data.attributes?.ctaLabel;
  const language = data.attributes?.language;

  const resourceUrl = getByType(data.attributes.related_resources, 'pdf')?.resourceUrl;
  const promoUrl = type === 'promo' ? document?.content?.find((o) => o._type === 'LINK')?._attributes?.href : null;

  const targetUrl = promoUrl ?? (resourceUrl && `${window.cobaltImgBaseUrl}${resourceUrl}`);

  const alternateHeadline = getByType(document?.headgroup, 'ALTERNATE-HEADLINE')?._html;
  const headline = ((isList && alternateHeadline) || getByType(document?.headgroup, 'HEADLINE')?._html) ?? data.title;

  const alternateSummary = getByType(document?._elements, 'ALTERNATE-SUMMARY')?._html;
  const summary = (isList && alternateSummary) || getByType(document?._elements, 'SUMMARY')?._html;

  const tag = data.attributes?.tagName;
  const hashtags = data.attributes?.hashtags;
  const contentScoring = data.attributes?.main?.contentScoring;
  const listname = data.attributes?.listName;

  const photos: MediaImage[] =
    type === 'gallery'
      ? document?.content
          ?.filter((o) => o._type === 'PHOTO')
          .map(getImage)
          .filter(Boolean)
      : null;
  const body =
    type === 'article' || type === 'card' || type === 'prizehubcard' || type === 'poll'
      ? document?.content?.map(mapByType).filter(Boolean)
      : null;

  let mainMedia = mapByType(document?.mediagroup?.[0]);
  let imageUrl = mainMedia?.image?.wide?.w1;

  const topcontent = document?.linkgroup?.find(
    (e) => e._attributes?.['emk-class'] && e._attributes['emk-class'] !== 'sourceCorrelation',
  );

  const media = topcontent?._attributes?.['data-id'] && nodes?.[topcontent._attributes['data-id']];

  const podcastUrl = document?.content?.find((o) => o._type === 'LINK' && o._attributes['emk-type'] === 'podcast-link')
    ?._attributes?.href;

  if (media) {
    mainMedia = mapEditorialContent(media);
    imageUrl = imageUrl ?? mainMedia?.image?.wide?.w1;
  }

  let sponsor: EditorialSponsor = null;
  let sponsors: EditorialSponsor[] = [];
  if (data?.attributes?.sponsor || data?.attributes?.sponsors) {
    sponsors = getSponsor(data.attributes);
    sponsor = sponsors?.[0];
  }

  return {
    id: data.id,
    hideFromWeb,
    type,
    title: stripHtml(headline),
    headline,
    targetUrl,
    htmlDescription: summary,
    description: stripHtml(summary),
    body,
    mediaParts: mediaInfo,
    imageUrl,
    mainMedia,
    photos,
    duration: parseNumber(mediaInfo?.duration),
    width: parseNumber(mediaInfo?.width),
    height: parseNumber(mediaInfo?.height),
    classification: data?.attributes?.main,
    hlsStreamUrl: mediaInfo?.mediaUrl,
    credits: mediaInfo?.credit,
    comment: mediaInfo?.caption,
    liveblogData: data?.attributes?.liveblogData,
    publicationDate: data?.attributes?.firstPublicationDate,
    newsDate: data?.attributes?.firstPublicationDate,
    firstPublicationDate: data?.attributes?.firstPublicationDate,
    lastModificationDate: data?.attributes?.lastPublicationDate,
    externalProduct: data?.attributes?.externalProducts?.[0],
    document,
    sponsor,
    sponsors,
    relations: data?.attributes?.footballEntities,
    podcastUrl,
    tag,
    listname,
    hashtags,
    contentScoring,
    ctaLabel,
    language,
  };
};

const getSponsor = (attributes: Record<string, any>): EditorialSponsor[] => {
  if (attributes.sponsor) {
    return [
      {
        name: attributes.sponsor,
        type: getSponsorType(attributes.sponsor),
        code: getSponsorType(attributes.sponsor),
      },
    ];
  } else {
    return attributes.sponsors.map((x: any) => ({ name: x.sponsorCode, type: x.sponsorCode, code: x.sponsorCode }));
  }
};

const getSponsorType = (sponsor: string) => {
  return sponsor.trim().replaceAll(' ', '-').replaceAll("'", '').replaceAll('.', '').toLowerCase();
};

export const mapEditorialContentMatchAtGlance = (
  data,
  nodes,
): EditorialContent & {
  matchAtGlanceTitle?: string;
  matchAtGlanceElements?: any[];
} => {
  const result = mapEditorialContent(data, nodes, false);

  const matchAtGlanceTitle =
    result?.document?.linkgroup?.length > 0 ? result?.document?.linkgroup[0]._html : 'Match at Glance';
  const elements = result?.document?.linkgroup.length > 1 ? result?.document?.linkgroup.slice(1) : [];
  const matchAtGlanceElements = elements
    .filter((e) => !nodes?.[e._attributes['data-id']]?.attributes.hideFromWeb)
    .map((e) => {
      const addContent = e._attributes?.['emk-class'] && e._attributes['emk-class'] !== 'sourceCorrelation';
      const media = addContent && e?._attributes?.['data-id'] && nodes?.[e._attributes['data-id']];
      e.id = e?._attributes?.['data-id'];
      if (media) {
        e.mainMedia = mapEditorialContent(media, null, true);
        e.imageUrl = e.imageUrl ?? e.mainMedia?.image?.wide?.w1;
      }
      return e;
    });
  return {
    ...result,
    matchAtGlanceTitle,
    matchAtGlanceElements,
  };
};

/**
 * Retrieves the ticketing information for the provided editorial content.
 * @param ticket - The editorial content to extract the ticketing information from.
 * @returns The ticketing information for the provided editorial content.
 */
export const getTicketInfo = (ticket: EditorialContent): TicketingInfo => {
  return ticket?.externalProduct?.ticketing?.[0];
};
